import React, { useEffect, useRef, useState } from 'react'
import { ChevronUp, ChevronDown } from 'react-feather'
import { Button } from '@achieve/ascend'
import Collapse from '@mui/material/Collapse'

/**
 * A React component that provides a collapsible container based on a specified maximum height.
 *
 * @component
 * @param {Object} props - The properties of the component.
 * @param {ReactNode} props.children - The content to be collapsed/expanded.
 * @param {number} props.collapseHeight - The maximum height at which the content should be collapsed.
 * @param {string} [props.showMoreText='Show more'] - The text to display on the "Show more" button.
 * @param {string} [props.showLessText='Show less'] - The text to display on the "Show less" button.
 * @returns {JSX.Element} - The rendered JSX element.
 */
const CollapseByHeight = ({
  children,
  collapseHeight = 500,
  showMoreText = 'Show more',
  showLessText = 'Show less',
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef(null)

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.clientHeight)
    }
  }, [children])

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div className={className} data-testid="collapse-by-height">
      <Collapse in={isExpanded} collapsedSize={`${collapseHeight}px`}>
        <div ref={contentRef}>{children}</div>
      </Collapse>
      {contentHeight > Number(collapseHeight) && (
        <Button
          endIcon={isExpanded ? <ChevronUp /> : <ChevronDown />}
          className={'w-full mt-4 mb-8 mx-0'}
          onClick={handleToggle}
          variant="text"
          data-testid="collapse-expand-button"
        >
          {isExpanded ? showLessText : showMoreText}
        </Button>
      )}
    </div>
  )
}

export default CollapseByHeight
